import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Boxes, Package, Tags, ArrowRight, Loader2, Bell, Send } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

const Dashboard = () => {
  const { email } = useAuth();
  const [stats, setStats] = useState({ brands: 0, products: 0, categories: 0 });
  const [loading, setLoading] = useState(true);
  const [push, setPush] = useState({ count: 0, enabled: false, loading: true });
  const [sending, setSending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let on = true;
    Promise.all([api.get('/brands'), api.get('/products'), api.get('/categories')])
      .then(([b, p, c]) => {
        if (!on) return;
        setStats({ brands: b.data.length, products: p.data.length, categories: c.data.length });
      })
      .finally(() => on && setLoading(false));

    api.get('/push/count')
      .then(({ data }) => on && setPush({ count: data.count, enabled: data.enabled, loading: false }))
      .catch(() => on && setPush(p => ({ ...p, loading: false })));

    return () => { on = false; };
  }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post('/push/test');
      toast({ title: 'Prueba enviada', description: `Se envió a ${push.count} suscripto(s)` });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo enviar' });
    } finally {
      setSending(false);
    }
  };

  const items = [
    { label: 'Marcas', value: stats.brands, icon: Boxes, to: '/admin/marcas', color: 'bg-emerald-50 text-emerald-700' },
    { label: 'Productos', value: stats.products, icon: Package, to: '/admin/productos', color: 'bg-blue-50 text-blue-700' },
    { label: 'Categorías', value: stats.categories, icon: Tags, to: '/admin/categorias', color: 'bg-amber-50 text-amber-700' },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-slate-500">Conectado como</p>
        <p className="mt-1 text-lg font-semibold text-slate-900">{email}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-700" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          {items.map(({ label, value, icon: Icon, to, color }) => (
            <Link
              key={label}
              to={to}
              className="group relative flex flex-col justify-between rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md"
            >
              <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-6">
                <p className="text-4xl font-bold tracking-tight text-slate-900">{value}</p>
                <p className="mt-1 text-sm text-slate-600">{label}</p>
              </div>
              <ArrowRight className="absolute right-5 top-5 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1 group-hover:text-emerald-700" />
            </Link>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">Notificaciones push</h3>
              <p className="mt-0.5 text-sm text-slate-600">
                {push.loading ? 'Cargando...' :
                  push.enabled
                    ? <>Hay <strong className="text-emerald-700">{push.count}</strong> vendedor(es) suscritos. Reciben un aviso cuando se agrega un producto, marca o post nuevo.</>
                    : 'Servicio no habilitado. Faltan claves VAPID en el servidor.'}
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={sendTest}
            disabled={sending || !push.enabled || push.count === 0}
            className="h-10 shrink-0 gap-2 bg-emerald-700 text-white hover:bg-emerald-800"
            data-testid="push-send-test"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar prueba
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
